import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Loader2, ShieldAlert } from 'lucide-react';

export const ROLE_OPTIONS = [
  ['learner', 'Learner', 'Individual / resident / staff learner — takes courses'],
  ['org_admin', 'Organisation Admin', 'Client portal only: manages their own staff, assignments and reports'],
  ['content_manager', 'Content Manager', 'Backend course building only — cannot delete courses or see billing'],
  ['super_admin', 'Super Admin', 'Full access to everything'],
];

// Super Admin: change a user's role (server enforces safety rules)
export const ChangeRoleDialog = ({ user, open, onOpenChange, onChanged }) => {
  const { api } = useAuth();
  const [role, setRole] = useState('learner');
  const [orgId, setOrgId] = useState('');
  const [orgs, setOrgs] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !user) return;
    setRole(user.role);
    setOrgId(user.organisation_id || '');
    api.get('/organisations').then(r => setOrgs(r.data?.organisations || r.data || [])).catch(() => setOrgs([]));
  }, [open, user, api]);

  const save = async () => {
    if (role === 'super_admin' && !window.confirm(`Give ${user.email} FULL Super Admin access?`)) return;
    setSaving(true);
    try {
      const res = await api.put(`/users/${user.id}/role`, { role, organisation_id: orgId || undefined });
      toast.success(res.data.changed ? 'Role updated' : 'Role unchanged');
      onChanged?.(res.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not change role');
    } finally {
      setSaving(false);
    }
  };

  const desc = ROLE_OPTIONS.find(r => r[0] === role)?.[2];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="change-role-dialog">
        <DialogHeader>
          <DialogTitle>Change role</DialogTitle>
          <DialogDescription>{user?.first_name} {user?.last_name} · {user?.email}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Role</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="change-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLE_OPTIONS.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`role-option-${v}`}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
            {desc && <p className="text-xs text-slate-500">{desc}</p>}
          </div>
          {role === 'org_admin' && (
            <div className="space-y-2">
              <Label>Organisation <span className="text-red-500">*</span></Label>
              <Select value={orgId} onValueChange={setOrgId}>
                <SelectTrigger data-testid="change-role-org-select"><SelectValue placeholder="Choose organisation" /></SelectTrigger>
                <SelectContent>
                  {(Array.isArray(orgs) ? orgs : []).map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {role === 'super_admin' && (
            <p className="text-xs text-amber-700 flex items-center gap-1.5"><ShieldAlert className="h-4 w-4" /> Super Admins can see billing data and delete courses.</p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving || (role === 'org_admin' && !orgId)} className="bg-[#20acf6] hover:bg-[#3bb8f7]" data-testid="change-role-save-btn">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Save role
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
